export type AgendaCalendarView = 'day' | 'week'

type AgendaRange = {
  start: Date
  end: Date
  rangeStart: string
  rangeEnd: string
}

const pad = (n: number) => String(n).padStart(2, '0')

const fmt = (d: Date, endOfDay = false) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${
    endOfDay ? '23:59:59' : '00:00:00'
  }`

export function getWeekRange(weekAnchor: Date): AgendaRange {
  const start = new Date(weekAnchor)
  start.setHours(0, 0, 0, 0)
  const day = start.getDay()
  const diff = day === 0 ? -6 : 1 - day
  start.setDate(start.getDate() + diff)

  // Semana útil: segunda a sexta
  const end = new Date(start)
  end.setDate(end.getDate() + 4)
  end.setHours(23, 59, 59, 999)

  return {
    start,
    end,
    rangeStart: fmt(start),
    rangeEnd: fmt(end, true),
  }
}

export function getDayRange(dayAnchor: Date): AgendaRange {
  const start = new Date(dayAnchor)
  start.setHours(0, 0, 0, 0)
  const end = new Date(dayAnchor)
  end.setHours(23, 59, 59, 999)

  return {
    start,
    end,
    rangeStart: fmt(start),
    rangeEnd: fmt(end, true),
  }
}

export function getAgendaRange(
  anchor: Date,
  view: AgendaCalendarView
): AgendaRange {
  return view === 'day' ? getDayRange(anchor) : getWeekRange(anchor)
}

function shortMonthPt(d: Date): string {
  return d
    .toLocaleDateString('pt-BR', { month: 'short' })
    .replace(/\./g, '')
    .trim()
    .toLowerCase()
}

function dayMonthCompact(d: Date): string {
  return `${String(d.getDate()).padStart(2, '0')}-${shortMonthPt(d)}`
}

export function formatWeekLabel(weekAnchor: Date): string {
  const { start, end } = getWeekRange(weekAnchor)
  const sameMonth =
    start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()
  const year = end.getFullYear()
  if (sameMonth) {
    const month = shortMonthPt(start)
    const dayStart = String(start.getDate()).padStart(2, '0')
    const dayEnd = String(end.getDate()).padStart(2, '0')
    return `${dayStart}–${dayEnd} ${month} ${year}`
  }
  return `${dayMonthCompact(start)} a ${dayMonthCompact(end)} ${year}`
}

export function formatDayLabel(dayAnchor: Date): string {
  return `${dayMonthCompact(dayAnchor)} ${dayAnchor.getFullYear()}`
}

export function formatAgendaLabel(anchor: Date, view: AgendaCalendarView): string {
  return view === 'day' ? formatDayLabel(anchor) : formatWeekLabel(anchor)
}

export function shiftCalendarAnchor(
  anchor: Date,
  view: AgendaCalendarView,
  delta: number
): Date {
  const next = new Date(anchor)
  if (view === 'day') {
    next.setDate(next.getDate() + delta)
  } else {
    next.setDate(next.getDate() + delta * 7)
  }
  return next
}
